import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

/**
 * Mono pill for machine facts and statuses — timestamps, refs, evidence,
 * sync health. Evidence chips end with `→` (they always link somewhere).
 *
 * @example
 * <Chip>receipt →</Chip>
 * <Chip tone="accent">needs you</Chip>
 * <Chip tone="honesty">stale · last sync 26h</Chip>
 */
const chipVariants = cva(
  "inline-flex items-center gap-[5px] font-mono font-medium text-[11px] leading-none " +
    "px-[9px] py-1 rounded-full border bg-[rgba(var(--glass-rgb),.45)] whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "border-[rgba(var(--ink-rgb),.13)] text-text-secondary",
        accent: "border-[var(--accent-border)] text-accent",
        success: "border-[var(--success-border)] text-success",
        honesty: "border-[var(--honesty-border)] text-[var(--honesty)]",
        incident: "border-[rgba(var(--incident-rgb),.4)] text-[var(--incident)]",
        dashed: "border-dashed border-[var(--dash-border)] text-[var(--text-quiet)]",
        training: "border-[var(--honesty-border)] bg-[var(--honesty-bg)] text-[var(--honesty)]",
      },
    },
    defaultVariants: { tone: "neutral" },
  }
);

type ChipProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof chipVariants>;

export function Chip({ className, tone, ...props }: ChipProps) {
  return <span className={cn(chipVariants({ tone }), className)} {...props} />;
}

export { chipVariants };
export type { ChipProps };
